import { Injectable } from '@angular/core';
import { BehaviorSubject, Subject, map } from 'rxjs';
import { Archetype, Lego } from '@domain/models/task/task';

@Injectable({
  providedIn: 'root'
})
export class CartService<T extends Lego | Archetype> {
  private readonly cartNotifier = new Subject<T>();
  private readonly cartWithTasksNotifier = new BehaviorSubject<T[]>([]);
  private readonly cartActionNotifier = new BehaviorSubject<{
    id: string;
    action: 'add' | 'remove' | 'none';
  }>({ id: '', action: 'none' });

  private tasksInCart: T[] = [];
  private readonly clearAllTasksNotifier = new Subject<void>();

  setCart(tasks: T[]) {
    this.tasksInCart = tasks;
  }

  getCart(): T[] {
    return this.tasksInCart;
  }

  getCartCount() {
    return this.tasksInCart.length;
  }

  listenCartState() {
    return this.cartWithTasksNotifier.asObservable();
  }

  emitCartState(tasks: T[]) {
    this.cartWithTasksNotifier.next(tasks);
  }

  whenCartChanged() {
    return this.cartActionNotifier.asObservable();
  }

  emitCartChanged(id: string, action: 'add' | 'remove') {
    this.cartActionNotifier.next({ id, action });
  }

  whenTaskAdded() {
    return this.cartNotifier.asObservable().pipe(
      map((task) => {
        const exists = this.tasksInCart.find((l) => l.id === task.id);
        this.tasksInCart = exists
          ? this.tasksInCart.filter((l) => l.id !== task.id)
          : [...this.tasksInCart, task];
        this.emitCartState(this.tasksInCart);
        return { task, exists };
      })
    );
  }

  clearAllTasks() {
    this.tasksInCart = [];
    this.emitCartState([]);
    this.clearAllTasksNotifier.next();
  }

  whenAllTasksCleared() {
    return this.clearAllTasksNotifier.asObservable();
  }

  addTaskToCart(cart: T) {
    this.cartNotifier.next(cart);
  }
}
